import { MouseEvent, memo, useId, useMemo, useRef, useState } from 'react';
import React from 'react';

import { CompositeFilterDescriptor, State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { FilterChangeEvent } from '@progress/kendo-react-data-tools';
import type { Identifier, XYCoord } from 'dnd-core';
import { useAccordionButton } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';

import { WorklistViewModel } from 'models';

import { useEvent } from 'core/hooks';
import { Accordion, Button, ButtonVariants, Filter, Label } from 'core/ui';

import { useColumnsGridState } from 'features/exam/hooks';
import { ExamsGridService } from 'features/exam/services/exam-grid-service';

import { RelativeDateFilter } from './RelativeDateFilter';
import { RelativeDateFilterField, RelativeDateFilterType } from '../utils/relative-date-filters';

import CollapseIcon from '../assets/collapse.svg?react';
import ExpandIcon from '../assets/expand.svg?react';
import OriginalHandleIcon from '../assets/handle.svg?react';
import { WorklistEditService } from '../services';
import { WorklistFilterAccordionItemProps } from '../types';
import { WorklistColumnSelection } from './WorklistColumnSelection';
import { WorklistFilterAccordionItem } from './WorklistFilterAccordionItem';
import { WorklistNameInput } from './WorklistNameInput';
import { WorklistSortSelection } from './WorklistSortSelection';

const ALL_COLUMN_DEFINITIONS = ExamsGridService.getColumns();
const ALL_GRID_COLUMN_DEFINITIONS = ExamsGridService.getGridColumns();
const ALL_FIELDS = ALL_COLUMN_DEFINITIONS.filter((column) => column.filter).map((column) => ({
  filter: column.filter,
  label: column.title,
  name: column.field,
  operators: column.operators ?? [],
}));

type DropIndicator = 'above' | 'below' | 'on' | 'none';

// Define a more detailed type for the draggable item
interface DraggableWorklistItem extends WorklistViewModel {
  originalIndexInAllWorklists: number; // Index in the flat allWorklists array
}

// Explicitly define props to include dndType for clarity
interface WorklistFieldFilterAccordionItemProps extends Omit<WorklistFilterAccordionItemProps, 'filterType'> {
  dndType: string;
  nestingLevel?: number;
}

export const WorklistFieldFilterAccordionItem = memo<WorklistFieldFilterAccordionItemProps>(
  ({
    worklist,
    allWorklists,
    isTopLevel = false,
    currentDataState,
    currentColumnsState,
    onCreateWorklist,
    onDeleteWorklist,
    onUpdateWorklist,
    patientId,
    dndType,
    nestingLevel = 0,
  }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);

    const [worklistName, setWorklistName] = useState(worklist.name);
    const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
    const [isItemsExpanded, setIsItemsExpanded] = useState(false);
    const [filterForm, setFilterForm] = useState(() => WorklistEditService.initializeWorklistEditModalDataState(worklist.dataState));
    const { columnsState: columnsStateForm, setColumnsState: setColumnsStateForm } = useColumnsGridState(worklist.columnState);
    const [sortForm, setSortForm] = useState(() => WorklistEditService.initializeWorklistEditModalSort(worklist.dataState));

    // Relative date filter state
    const [relativeDateFilterField, setRelativeDateFilterField] = useState<RelativeDateFilterField | null>(() => {
      try {
        const dataState = JSON.parse(worklist.dataState || '{}');
        if (dataState.relativeDateFilters && Array.isArray(dataState.relativeDateFilters) && dataState.relativeDateFilters.length > 0) {
          return dataState.relativeDateFilters[0].field;
        }
      } catch (error) {
        console.warn('Failed to parse worklist dataState for relative date filters:', error);
      }
      return null;
    });
    const [relativeDateFilterType, setRelativeDateFilterType] = useState<RelativeDateFilterType | null>(() => {
      try {
        const dataState = JSON.parse(worklist.dataState || '{}');
        if (dataState.relativeDateFilters && Array.isArray(dataState.relativeDateFilters) && dataState.relativeDateFilters.length > 0) {
          return dataState.relativeDateFilters[0].filterType;
        }
      } catch (error) {
        console.warn('Failed to parse worklist dataState for relative date filters:', error);
      }
      return null;
    });

    const childWorklists = useMemo(
      () => allWorklists.filter((w) => w.parentViewId === worklist.id).sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
      [allWorklists, worklist],
    );

    const [{ isDragging }, drag, preview] = useDrag({
      type: dndType,
      item: (): DraggableWorklistItem => {
        console.log('[WorklistFieldFilterAccordionItem] useDrag ITEM factory triggered', { worklistId: worklist.id });
        return {
          ...worklist,
          originalIndexInAllWorklists: allWorklists.findIndex((w) => w.id === worklist.id),
        };
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [{ isOver, dropIndicator }, drop] = useDrop<DraggableWorklistItem, undefined, { isOver: boolean; dropIndicator: DropIndicator }>({
      accept: dndType,
      collect(monitor) {
        let indicator: DropIndicator = 'none';
        if (monitor.isOver({ shallow: true })) {
          const clientOffset = monitor.getClientOffset();
          const hoverBoundingRect = headerRef.current?.getBoundingClientRect();

          if (clientOffset && hoverBoundingRect) {
            const dropZoneRatio = (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height;
            if (dropZoneRatio > 0.25 && dropZoneRatio < 0.75) {
              indicator = 'on';
            } else if (dropZoneRatio <= 0.25) {
              indicator = 'above';
            } else {
              indicator = 'below';
            }
          }
        }
        return {
          isOver: monitor.isOver({ shallow: true }),
          dropIndicator: indicator,
        };
      },
      hover(draggedItem: DraggableWorklistItem, monitor) {
        if (!ref.current || draggedItem.id === worklist.id) return;
      },
      drop(draggedItem: DraggableWorklistItem, monitor) {
        console.log('[WorklistFieldFilterAccordionItem] DROP triggered', { draggedId: draggedItem.id, targetId: worklist.id });
        if (!ref.current || !headerRef.current || draggedItem.id === worklist.id) return;

        const dropTargetItem = worklist;
        const mutableAllWorklists = [...allWorklists];
        const itemsToUpdateMap = new Map<number, WorklistViewModel>();

        const recordUpdate = (itemToUpdate: Partial<WorklistViewModel> & { id: number }) => {
          const existingItemInAll = mutableAllWorklists.find((w) => w.id === itemToUpdate.id)!;
          const existingItemInMap = itemsToUpdateMap.get(itemToUpdate.id);
          itemsToUpdateMap.set(itemToUpdate.id, { ...existingItemInAll, ...existingItemInMap, ...itemToUpdate });
        };

        const reordinalizeChildrenOf = (parentId: number | null) => {
          const children = mutableAllWorklists
            .map((w) => itemsToUpdateMap.get(w.id) || w)
            .filter((w) => w.parentViewId === parentId)
            .sort((a, b) => {
              const ordA = a.ordinal ?? Infinity;
              const ordB = b.ordinal ?? Infinity;
              if (ordA === ordB) return (a.name || '').localeCompare(b.name || '');
              return ordA - ordB;
            });

          children.forEach((child, index) => {
            const currentChildState = itemsToUpdateMap.get(child.id) || child;
            if (currentChildState.ordinal !== index || currentChildState.parentViewId !== parentId) {
              recordUpdate({ ...currentChildState, id: child.id, ordinal: index, parentViewId: parentId });
            }
          });
        };

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) {
          console.error('Could not get client offset on drop. Aborting.');
          return;
        }

        const hoverBoundingRect = headerRef.current.getBoundingClientRect();

        const draggedItemCurrentState = itemsToUpdateMap.get(draggedItem.id) || mutableAllWorklists.find((w) => w.id === draggedItem.id)!;

        let tempParent = dropTargetItem.parentViewId;
        while (tempParent !== null) {
          if (tempParent === draggedItemCurrentState.id) return;
          const parentWorklist = mutableAllWorklists.find((w) => w.id === tempParent);
          tempParent = parentWorklist ? parentWorklist.parentViewId : null;
        }

        if (
          (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height > 0.25 &&
          (clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height < 0.75 &&
          draggedItemCurrentState.parentViewId !== dropTargetItem.id &&
          draggedItemCurrentState.id !== dropTargetItem.parentViewId
        ) {
          const oldParentId = draggedItemCurrentState.parentViewId;
          recordUpdate({ id: draggedItemCurrentState.id, parentViewId: dropTargetItem.id, ordinal: 100000 });

          reordinalizeChildrenOf(dropTargetItem.id);
          if (oldParentId !== dropTargetItem.id) {
            reordinalizeChildrenOf(oldParentId);
          }
        } else {
          const targetParentId = dropTargetItem.parentViewId;
          const originalDraggedItemParentId = draggedItemCurrentState.parentViewId;

          if (originalDraggedItemParentId !== targetParentId) {
            recordUpdate({ id: draggedItemCurrentState.id, parentViewId: targetParentId });
          }

          const tempTargetOrdinal = itemsToUpdateMap.get(dropTargetItem.id)?.ordinal ?? dropTargetItem.ordinal ?? 0;
          if ((clientOffset.y - hoverBoundingRect.top) / hoverBoundingRect.height <= 0.5) {
            recordUpdate({ id: draggedItemCurrentState.id, ordinal: tempTargetOrdinal - 0.5 });
          } else {
            recordUpdate({ id: draggedItemCurrentState.id, ordinal: tempTargetOrdinal + 0.5 });
          }
          reordinalizeChildrenOf(targetParentId);

          if (originalDraggedItemParentId !== targetParentId) {
            reordinalizeChildrenOf(originalDraggedItemParentId);
          }
        }

        const finalUpdatedWorklists = Array.from(itemsToUpdateMap.values());
        if (finalUpdatedWorklists.length > 0) {
          onUpdateWorklist(finalUpdatedWorklists);
        }
      },
    });

    drop(ref);
    preview(ref);

    // handle filter accordion click
    const toggleFiltersAccordionClick = useAccordionButton(`${worklist.id}-filters`, () => {
      setIsFiltersExpanded(!isFiltersExpanded);
    });

    // handle items accordion click
    const toggleItemsAccordionClick = useAccordionButton(`${worklist.id}-items`, () => setIsItemsExpanded(!isItemsExpanded));

    // handle filter change
    const handleFilterChange = useEvent((event: FilterChangeEvent) => {
      const newFilterForm: CompositeFilterDescriptor = {
        ...filterForm,
        ...event.filter,
      };

      setFilterForm(newFilterForm);
    });

    // Handle relative date filter changes
    const handleRelativeDateFilterChange = useEvent((field: RelativeDateFilterField, filterType: RelativeDateFilterType) => {
      setRelativeDateFilterField(field);
      setRelativeDateFilterType(filterType);
    });

    const handleRelativeDateFilterClear = useEvent(() => {
      setRelativeDateFilterField(null);
      setRelativeDateFilterType(null);
    });

    // handle save click for field filters
    const handleSaveClick = useEvent((event: MouseEvent<HTMLButtonElement>) => {
      if (sortForm.field == null) throw new Error('Sort field is required.');
      if (sortForm.dir == null) throw new Error('Sort direction is required.');

      toggleFiltersAccordionClick(event);

      let newDataState: State = {
        filter: filterForm,
        sort: [{ field: sortForm.field, dir: sortForm.dir }],
      };

      // Add relative date filter to dataState if specified
      if (relativeDateFilterField && relativeDateFilterType) {
        const config = ExamsGridService.createRelativeDateFilterConfig(relativeDateFilterField, relativeDateFilterType);
        const updatedDataStateJson = ExamsGridService.saveRelativeDateFilterToWorklist(JSON.stringify(newDataState), config);
        newDataState = JSON.parse(updatedDataStateJson);
      }

      onUpdateWorklist([
        {
          ...worklist,
          name: worklistName,
          dataState: JSON.stringify(newDataState),
          columnState: JSON.stringify(columnsStateForm),
          oDataResourceString: toDataSourceRequestString(newDataState),
        },
      ]);
    });

    // handle self create worklist
    const handleSelfCreateWorklist = useEvent((name: string) => {
      // This callback is used to create a new worklist under the current worklist.  So we need to pass in the current worklist id
      onCreateWorklist(name, isTopLevel ? null : worklist.id);
    });

    // handle delete worklist click
    const handleDeleteWorklistClick = useEvent(() => {
      onDeleteWorklist(worklist);
    });

    return (
      <div ref={ref} style={{ opacity: isDragging ? 0.4 : 1, position: 'relative' }}>
        {isOver && dropIndicator === 'above' && <StyledDropIndicator style={{ top: 0 }} />}
        <StyledHeaderWrapperDiv
          ref={headerRef}
          $isExpanded={isItemsExpanded || isFiltersExpanded}
          $isTopLevel={isTopLevel}
          $isDropTarget={isOver && dropIndicator === 'on'}
          $nestingLevel={nestingLevel}
        >
          {childWorklists.length > 0 &&
            (isItemsExpanded ? <StyledCollapseIcon onClick={toggleItemsAccordionClick} /> : <StyledExpandIcon onClick={toggleItemsAccordionClick} />)}
          {isFiltersExpanded ? (
            <StyledEditWorklistHeaderDiv>
              <StyledEditNameWorklistInput initialValue={worklistName} saveButtonText="Save Name" value={worklistName} onChange={setWorklistName} />
              <StyledSaveWorklistButton type="button" onClick={handleSaveClick}>
                Save
              </StyledSaveWorklistButton>
            </StyledEditWorklistHeaderDiv>
          ) : (
            <StyledHeaderDiv>
              <StyledNameDiv>{worklist.name}</StyledNameDiv>
              <StyledActionsDiv>
                <Button type="button" variant={ButtonVariants.SECONDARY} onClick={toggleFiltersAccordionClick}>
                  Edit
                </Button>
                <Button type="button" variant={ButtonVariants.SECONDARY} onClick={handleDeleteWorklistClick}>
                  Delete
                </Button>
              </StyledActionsDiv>
            </StyledHeaderDiv>
          )}
          <StyledHandleContainer ref={drag}>
            <StyledHandleIcon />
          </StyledHandleContainer>
        </StyledHeaderWrapperDiv>
        {isOver && dropIndicator === 'below' && <StyledDropIndicator style={{ bottom: 0 }} />}
        <StyledFiltersAccordionCollapse eventKey={`${worklist.id}-filters`}>
          <StyledBodyWrapperDiv>
            <StyledLabelDiv>FILTERS</StyledLabelDiv>
            <Filter fields={ALL_FIELDS} value={filterForm} onChange={handleFilterChange} />
            <StyledLabelDiv>RELATIVE DATE FILTER</StyledLabelDiv>
            <RelativeDateFilter
              selectedField={relativeDateFilterField}
              selectedFilterType={relativeDateFilterType}
              onFilterChange={handleRelativeDateFilterChange}
              onClearFilter={handleRelativeDateFilterClear}
            />
            <StyledLabelDiv>COLUMNS</StyledLabelDiv>
            <StyledColumnWrapperDiv>
              <WorklistColumnSelection columns={ALL_GRID_COLUMN_DEFINITIONS} columnsState={columnsStateForm} onChange={setColumnsStateForm} />
            </StyledColumnWrapperDiv>
            <StyledLabelDiv>SORT</StyledLabelDiv>
            <StyledColumnWrapperDiv>
              <StyledWorklistSortSelection columns={ALL_GRID_COLUMN_DEFINITIONS} onChange={setSortForm} field={sortForm.field} dir={sortForm.dir} />
            </StyledColumnWrapperDiv>
          </StyledBodyWrapperDiv>
        </StyledFiltersAccordionCollapse>
        <StyledItemsAccordionCollapse eventKey={`${worklist.id}-items`}>
          <StyledBodyWrapperDiv>
            <StyledLabelDiv>CHILD WORKLISTS</StyledLabelDiv>
            {childWorklists.map((childItem) => (
              <WorklistFilterAccordionItem
                key={childItem.id}
                allWorklists={allWorklists}
                worklist={childItem}
                currentDataState={currentDataState}
                currentColumnsState={currentColumnsState}
                onCreateWorklist={onCreateWorklist}
                onDeleteWorklist={onDeleteWorklist}
                onUpdateWorklist={onUpdateWorklist}
                filterType={childItem.entity === 'PatientFile' ? 'tag' : 'field'}
                patientId={patientId}
                dndType={dndType}
                nestingLevel={nestingLevel + 1}
              />
            ))}
            <StyledCreateWorklistNameInput onSave={handleSelfCreateWorklist} saveButtonText="Create Worklist" />
          </StyledBodyWrapperDiv>
        </StyledItemsAccordionCollapse>
      </div>
    );
  },
);

WorklistFieldFilterAccordionItem.displayName = 'WorklistFieldFilterAccordionItem';

const StyledHeaderWrapperDiv = styled.div<{
  $isExpanded: boolean;
  $isTopLevel: boolean;
  $isDropTarget?: boolean;
  $nestingLevel: number;
}>`
  position: relative;
  align-items: center;
  display: flex;
  margin-left: ${({ $isTopLevel, theme, $nestingLevel }) => ($isTopLevel ? 0 : parseInt(theme.space.spacing60, 10) + $nestingLevel * 15)}px;
  margin-bottom: ${({ $isExpanded, theme }) => ($isExpanded ? 0 : theme.space.spacing30)};
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  ${({ $isDropTarget, theme }) =>
    $isDropTarget &&
    `
    outline: 2px solid ${theme.colors.primary};
    background-color: ${theme.colors.palette.blues[0]};
  `}
`;

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${({ theme }) => theme.space.spacing40};
`;

const StyledEditWorklistHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledEditNameWorklistInput = styled(WorklistNameInput)`
  flex: 1;
`;

const StyledSaveWorklistButton = styled(Button)`
  white-space: nowrap;
`;

const StyledNameDiv = styled.div`
  flex: 1;
`;

const StyledActionsDiv = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledCollapseIcon = styled(CollapseIcon)`
  margin-right: ${({ theme }) => theme.space.spacing30};
  cursor: pointer;
`;

const StyledExpandIcon = styled(ExpandIcon)`
  margin-right: ${({ theme }) => theme.space.spacing30};
  cursor: pointer;
`;

const StyledFiltersAccordionCollapse = styled(Accordion.Collapse)`
  margin-left: ${({ theme }) => theme.space.spacing60};
`;

const StyledItemsAccordionCollapse = styled(Accordion.Collapse)`
  margin-left: ${({ theme }) => theme.space.spacing60};
`;

const StyledBodyWrapperDiv = styled.div`
  padding: ${({ theme }) => theme.space.spacing40};
`;

const StyledColumnWrapperDiv = styled.div`
  max-width: 600px;
`;

const StyledWorklistSortSelection = styled(WorklistSortSelection)`
  max-width: 400px;
`;

const StyledLabelDiv = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  margin-bottom: ${({ theme }) => theme.space.spacing20};
  margin-top: ${({ theme }) => theme.space.spacing40};

  &:first-child {
    margin-top: 0;
  }
`;

const StyledHandleContainer = styled.div`
  cursor: move;
  margin-left: ${({ theme }) => theme.space.spacing40};
  display: flex;
  align-items: center;
`;

const StyledHandleIcon = styled(OriginalHandleIcon)`
  fill: ${({ theme }) => theme.colors.palette.grayscale[7]};
`;

const StyledDropIndicator = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: ${({ theme }) => theme.colors.primary};
  z-index: 1;
`;

const StyledCreateWorklistNameInput = styled(WorklistNameInput)`
  flex: 1;
`;
